import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, AlertCircle, Wallet, Loader2 } from 'lucide-react';
import { financialsConnectionApi } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

type TestResult = { ok: boolean; consumer?: string; scope?: string; detail?: string } | null;

/**
 * The financials connection — the credential, not the time.
 *
 * ⚠ THE KEY IS NEVER DISPLAYED. The server returns only the last four
 * characters, which is enough to tell two keys apart and useless to anyone who
 * shoulder-reads a screenshot or a support session. Leaving the field blank on
 * save keeps the stored key and changes only the URL — the browser has no copy
 * to send back, so an empty field must mean "unchanged", never "erase".
 */
export function FinancialsCard() {
  const queryClient = useQueryClient();
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [testResult, setTestResult] = useState<TestResult>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['financials-connection'],
    queryFn: () => financialsConnectionApi.get(),
  });

  useEffect(() => {
    if (data) setBaseUrl(data.stored?.baseUrl ?? data.active?.baseUrl ?? '');
  }, [data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['financials-connection'] });
    // Status and hours read the same credentials, so they are stale the moment
    // this changes.
    queryClient.invalidateQueries({ queryKey: ['financials-hours'] });
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      financialsConnectionApi.save({
        baseUrl: baseUrl.trim(),
        apiKey: apiKey.trim() || undefined,
        enabled: true,
      }),
    onSuccess: () => {
      setApiKey('');
      setTestResult(null);
      invalidate();
    },
  });

  const testMutation = useMutation({
    mutationFn: () =>
      financialsConnectionApi.test({ baseUrl: baseUrl.trim(), apiKey: apiKey.trim() || undefined }),
    onSuccess: (result) => setTestResult(result),
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      financialsConnectionApi.save({ baseUrl: baseUrl.trim(), enabled }),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: () => financialsConnectionApi.remove(),
    onSuccess: () => {
      setApiKey('');
      setTestResult(null);
      invalidate();
    },
  });

  const active = data?.active ?? null;
  const stored = data?.stored ?? null;
  const hasKey = !!stored?.apiKeyHint;
  const canSave = baseUrl.trim().length > 0 && (hasKey || apiKey.trim().length > 0);

  return (
    <Card className="p-6" data-testid="financials-integration">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
          <Wallet className="h-5 w-5 text-accent" />
        </div>
        <div className="flex-1">
          <h3 className="text-heading-3 text-secondary">Solid Financials</h3>
          <p className="text-sm text-grey-500">
            Time tracking and support hours, read from financials
          </p>
        </div>
        {/* Three states, not two. "Credentials are on file" and "they work" are
            different facts, and showing the first as the second put a green
            Connected badge directly above "Invalid X-API-Key". */}
        {isLoading ? null : active?.reachable ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-1 text-xs font-medium text-success">
            <CheckCircle className="h-3 w-3" />
            Connected
          </span>
        ) : active ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-error/10 px-2 py-1 text-xs font-medium text-error">
            <AlertCircle className="h-3 w-3" />
            Key not working
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-1 text-xs font-medium text-warning">
            <AlertCircle className="h-3 w-3" />
            Not connected
          </span>
        )}
      </div>

      {/* An env-configured deployment is genuinely connected, and a screen that
          said "not connected" because the DB row is empty would send somebody
          hunting a bug that isn't one. */}
      {/* Say which key is failing, so the fix is obvious rather than guessed. */}
      {active && !active.reachable && (
        <p className="mb-4 rounded-lg bg-error/5 p-3 text-sm text-error">
          A key is stored, but financials rejects it. Use an Accounts service key created
          under the <strong>Solid Hub</strong> application — the key names the caller, and
          the caller is the Hub. A key created under the Financials application identifies
          financials itself and is refused by name. (Financials&rsquo; own{' '}
          <code className="font-mono">fin_time_</code> env key also works; a Hub{' '}
          <code className="font-mono">shk_</code> key never does.)
        </p>
      )}

      {active?.source === 'environment' && (
        <p className="mb-4 rounded-lg bg-grey-50 p-3 text-sm text-grey-600">
          Currently using credentials from this deployment&rsquo;s environment. Saving a key here
          replaces them, and it can then be rotated without a deploy.
        </p>
      )}

      <div className="space-y-3 border-t border-grey-100 pt-4">
        <div>
          <label htmlFor="financials-url" className="mb-1 block text-sm font-medium text-secondary">
            Financials URL
          </label>
          <input
            id="financials-url"
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://financials.soliddigital.com"
            className="input"
          />
          <p className="mt-1 text-xs text-grey-500">Base URL only — no path.</p>
        </div>

        <div>
          <label htmlFor="financials-key" className="mb-1 block text-sm font-medium text-secondary">
            Service API key
          </label>
          <input
            id="financials-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              setTestResult(null);
            }}
            placeholder={hasKey ? `Stored — ends ${stored?.apiKeyHint}` : 'Paste the key from financials'}
            className="input font-mono"
          />
          <p className="mt-1 text-xs text-grey-500">
            {/* The key names the CALLER, and the caller is this app — so it is
                created under the Solid Hub application in Accounts, never under
                Financials. A key created under Financials means "financials is
                calling", and the audit log over there would attribute every
                hour the hub writes to financials itself. */}
            {hasKey
              ? 'Leave blank to keep the stored key. Encrypted at rest; never shown again.'
              : 'Create this in Accounts under the Solid Hub application (Service Keys) — it identifies the Hub as the caller. Encrypted at rest; never shown again.'}
          </p>
        </div>

        {testResult && (
          <p
            className={cn(
              'flex items-start gap-1.5 text-sm',
              testResult.ok ? 'text-success' : 'text-error',
            )}
            role="status"
          >
            {testResult.ok ? (
              <>
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Reached financials as “{testResult.consumer}”
                  {/* The scope IS the security story: a key that is not
                      self-scoped could reach other people's time. */}
                  {testResult.scope && `, scope “${testResult.scope}”`}.
                </span>
              </>
            ) : (
              <>
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{testResult.detail || 'Could not reach financials.'}</span>
              </>
            )}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!canSave || saveMutation.isPending}
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
          <Button
            variant="outline"
            onClick={() => testMutation.mutate()}
            disabled={!baseUrl.trim() || testMutation.isPending}
          >
            {testMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Test connection
          </Button>

          <div className="flex-1" />

          {stored && (
            <>
              <Button
                variant="ghost"
                onClick={() => toggleMutation.mutate(!stored.enabled)}
                disabled={toggleMutation.isPending}
              >
                {stored.enabled ? 'Disable' : 'Enable'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => removeMutation.mutate()}
                disabled={removeMutation.isPending}
                className="text-error hover:bg-error/10"
              >
                Remove
              </Button>
            </>
          )}
        </div>

        {stored && !stored.enabled && (
          <p className="text-sm text-warning">
            Disabled. Time tracking and support hours are hidden until this is enabled again.
          </p>
        )}
      </div>
    </Card>
  );
}
